import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { useTranslation } from "react-i18next";
import PrimeTable from "../../PrimeTable/PrimeTable";
import { useGetFinantialInstitutions } from "../OpenBankingService";
import * as xlsx from "xlsx";
import func from "../../../services/functions";

const PreviewEmployeeReferenceData = ({ visible, onHide }) => {
  const { t } = useTranslation("translations");
  const { data: financialInstitutions, loading: banksLoading } = useGetFinantialInstitutions();
  const [banksData, setBanksData] = useState([]);
  const [currenciesData, setCurrenciesData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && currenciesData.length === 0) {
      loadCurrencies();
    }
  }, [visible]);

  useEffect(() => {
    if (financialInstitutions && financialInstitutions.length > 0) {
      setBanksData(
        financialInstitutions.map((bank, idx) => ({
          id: idx,
          bic: bank.bic || bank.code || "",
          bank_name: bank.name || bank.institutionName || "",
        }))
      );
    }
  }, [financialInstitutions]);

  const loadCurrencies = async () => {
    setLoading(true);
    try {
      const currenciesResponse = await fetch("/reference_currencies.csv");
      const currenciesText = await currenciesResponse.text();
      const currenciesWorkbook = xlsx.read(currenciesText, { type: "string" });
      const currenciesRows = xlsx.utils.sheet_to_json(
        currenciesWorkbook.Sheets[currenciesWorkbook.SheetNames[0]]
      );
      setCurrenciesData(
        currenciesRows
          .filter((row) => row.currency_code)
          .map((row, idx) => ({
            id: idx,
            currency_code: row.currency_code || "",
            currency_name: row.currency_name || "",
          }))
      );
    } catch (error) {
      console.error("Error loading currencies:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = (filename) => {
    try {
      if (filename === "banks") {
        const rows = [
          ["bic", "bank_name"],
          ...banksData.map((bank) => [bank.bic, bank.bank_name]),
        ];
        func.exportToCsv("financial_institutions.csv", rows);
      } else if (filename === "currencies") {
        const link = document.createElement("a");
        link.href = "/reference_currencies.csv";
        link.download = "reference_currencies.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Error downloading:", error);
    }
  };

  const banksColumns = [
    {
      header: t("exports.bicCode"),
      field: "bic",
      style: { minWidth: "140px", maxWidth: "200px" },
      sortable: true,
      filter: true,
    },
    {
      header: t("exports.bankName"),
      field: "bank_name",
      style: { minWidth: "200px", maxWidth: "350px" },
      sortable: true,
      filter: true,
    },
  ];

  const currenciesColumns = [
    {
      header: "Code",
      field: "currency_code",
      style: { minWidth: "100px", maxWidth: "150px" },
      sortable: true,
      filter: true,
    },
    {
      header: "Currency",
      field: "currency_name",
      style: { minWidth: "150px", maxWidth: "300px" },
      sortable: true,
      filter: true,
    },
  ];

  const downloadButton = (downloadKey) => {
    return downloadKey === "banks" ? (
      <button
        onClick={() => downloadCSV(downloadKey)}
        className="btn btn-primary shadow-sm btn-xs text-light"
      >
        <i className="pi pi-download mr-2"></i>
        {t("exports.download")}
      </button>
    ) : (
      <a
        href="/reference_currencies.csv"
        className="btn btn-primary shadow-sm btn-xs text-light"
        download="reference_currencies.csv"
      >
        <i className="pi pi-download mr-2"></i>
        {t("exports.download")}
      </a>
    );
  };

  const gridTemplate = (data, columns, downloadKey) => {
    const headerButtonsLeft = downloadButton(downloadKey);
    return (
      <div style={{overflow:"none"}}>
        <PrimeTable
          value={data}
          columns={columns}
          simpleTable={true}
          compact={true}
          showFilters={false}
          paginator={true}
          showSelectColumns={false}
          filterDisplay={false}
          responsiveLayout="scroll"
          showGlobalSearch={true}
          searchableKeys={columns.map((col) => col.field)}
          loading={loading || banksLoading}
          dataKey="id"
          headerButtonsLeft={headerButtonsLeft}
        />
      </div>
    );
  };

  return (
    <Dialog
      header={t("exports.referenceData")}
      visible={visible}
      onHide={onHide}
      style={{ width: "40vw",minHeight:400,maxHeight:"95vh", display: "flex", flexDirection: "column" }}
      breakpoints={{ "960px": "85vw", "641px": "100vw" }}
      modal
      contentStyle={{ paddingBottom: 0,flexGrow:1}}
    >
        <div style={{overflow:"none"}}>
          <TabView style={{margin:0}}>
            <TabPanel style={{margin:0}} header={t("mySuppliers.financialInstitution")}>
              {gridTemplate(banksData, banksColumns, "banks")}
            </TabPanel>

            <TabPanel style={{margin:0}} header="Currency">
              {gridTemplate(currenciesData, currenciesColumns, "currencies")}
            </TabPanel>
          </TabView>
        </div>
    </Dialog>
  );
};

export default PreviewEmployeeReferenceData;
